import React, { useEffect } from 'react';
import { defineMessages } from 'react-intl';
import { CheckOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import ListPage from '@components/common/layout/ListPage';
import { DEFAULT_FORMAT, DEFAULT_TABLE_ITEM_SIZE_LARGE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { deviceKindOptions, deviceSubKindOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseTable,BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime, copyToClipboard } from '@utils';
import { Button, Modal } from 'antd';

const message = defineMessages({
    objectName: 'Tablet Request',
});

const DigibesRequestTab = ({ isActive }) => {
    const translate = useTranslate();
    const notification = useNotification();
    const deviceKindOpt = translate.formatKeys(deviceKindOptions, ['label']);
    const deviceSubKindOpt = translate.formatKeys(deviceSubKindOptions, ['label']);

    const { execute: executeApprove } = useFetch(apiConfig.tableRequest.approve);
    const { data, mixinFuncs, pagination, loading } = useListBase({
        apiConfig: apiConfig.tableRequest,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.additionalActionColumnButtons = () => ({
                copy: (record) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.copy)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                                copyToClipboard(record?.deviceToken, translate);
                            }}
                        >
                            <CopyOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                approve: ({ id }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.approve)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                                Modal.confirm({
                                    title: 'Approve Tablet Request',
                                    content: 'Are you sure you want to approve this request?',
                                    okText: 'Approve',
                                    okType: 'primary',
                                    cancelText: 'Cancel',
                                    onOk: () => {
                                        executeApprove({
                                            data: {
                                                id: id,
                                            },
                                            onCompleted: (res) => {
                                                if (res.result) {
                                                    notification({ type: 'success', message: 'Approve success' });
                                                    mixinFuncs.getList();
                                                }
                                            },
                                            onError: (error) => {
                                                console.log(error);
                                            },
                                        });
                                    },
                                });
                            }}
                        >
                            <CheckOutlined
                                style={{
                                    color: '#52c41a',
                                }}
                            />
                        </Button>
                    </BaseTooltip>
                ),
                delete: ({ id }) => (
                    <BaseTooltip type="delete" objectName={translate.formatMessage(message.objectName)}>
                        <Button
                            type="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                mixinFuncs.showDeleteItemConfirm(id);
                            }}
                            style={{ padding: 0 }}
                        >
                            <DeleteOutlined style={{ color: 'red' }} />
                        </Button>
                    </BaseTooltip>
                ),
            });
        },
    });

    const columns = [
        {
            title: translate.formatMessage(commonMessage.name),
            dataIndex: 'name',
        },
        {
            title: translate.formatMessage(commonMessage.deviceToken),
            dataIndex: 'deviceToken',
        },
        {
            title: translate.formatMessage(commonMessage.deviceKind),
            dataIndex: 'kind',
            render: (kind) => {
                return <>{deviceKindOpt?.find((item) => item.value === kind)?.label}</>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.createdDate),
            width: 200,
            dataIndex: 'createdDate',
            render: (createdDate) => {
                const createDateLocal = convertUtcToLocalTime(createdDate, DEFAULT_FORMAT, DEFAULT_FORMAT);
                return <div>{createDateLocal}</div>;
            },
        },

        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                copy: true,
                // approve: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    useEffect(() => {
        if (!isActive) return;
        mixinFuncs.getList();
        const interval = setInterval(() => {
            mixinFuncs.getList();
        }, 5000);

        return () => clearInterval(interval);
    }, [isActive]);

    return (
        <ListPage
            baseTable={
                <BaseTable
                    onChange={mixinFuncs.changePagination}
                    columns={columns}
                    dataSource={data}
                    pagination={pagination}
                    // loading={loading}
                />
            }
        />
    );
};

export default DigibesRequestTab;
